import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";

import NestedTable from "../../components/Tables/NestedTable";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import BaseActionButton from "../../components/Buttons/BaseActionButton";
import BaseUnitModal from "../../components/Modals/BaseUnitModal";
import ConversionUnitModal from "../../components/Modals/ConversionUnitModal";
import UnitsAPI from "../../API/units.api";
import { getObject, setObject } from "../../utils/StorageHelpers";
import { longDateTime } from "../../utils/DateHelper";
import { getMappedFilters, getSessionFilters } from "../../utils/FilterHelpers";
import { SuccessAlert } from "../../utils/Alerts";
import UnitConversionAPI from "../../API/unitConversion.api";
import DeleteButton from "../../components/Buttons/DeleteButton";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import Tippy from "@tippyjs/react";

const UnitsManagement = () => {
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("unit-management-filters")
    );
    const [isLoading, setIsLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalUnitsCount, setTotalUnitsCount] = useState(0);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Unit-Management-itemsPerPage", true) || 10
    );

    const [filtersUsed, setFiltersUsed] = useState(false);

    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const [units, setUnits] = useState([]);

    const [baseUnitModal, setBaseUnitModal] = useState(false);
    const [selectedBaseUnit, setSelectedBaseUnit] = useState({});

    const [conversionUnitModal, setConversionUnitModal] = useState(false);
    const [selectedConvertionUnit, setSelectedConvertionUnit] = useState({});

    const [deleteModal, setDeleteModal] = useState(false);
    const [toBeDeletedId, setToBeDeletedId] = useState("");

    const headers = [
        {
            skipSort: true,
            label: t("unit"),
            name: "unit",
        },
        {
            label: t("createdAt"),
            name: "createdAt",
        },
        {
            skipSort: true,
            label: t("actions"),
            name: "actions",
        },
    ];

    const subHeaders = [
        {
            skipSort: true,
            label: t("unit"),
            name: "unit",
        },
        {
            skipSort: true,
            label: t("equation"),
            name: "equation",
        },
        {
            skipSort: true,
            label: t("description"),
            name: "description",
        },
        {
            skipSort: true,
            label: t("actions"),
            name: "actions",
        },
    ];

    const keys = [
        {
            name: "unit",
        },
        {
            name: "createdAt",
            parseMethod: longDateTime,
        },
        {
            name: "actions",
            element: (row) => (
                <div className="flex items-center justify-center">
                    <div className="mr-2">
                        <BaseActionButton
                            className="table-btn table-btn-blue"
                            icon="fas fa-plus"
                            tippy={true}
                            tippySettings={{
                                content: t("addConversionUnits"),
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedConvertionUnit({
                                    baseUnitId: row.id,
                                });
                                setConversionUnitModal(true);
                            }}
                        />
                    </div>
                    <BaseActionButton
                        className="table-btn table-btn-blue"
                        icon="fas fa-tools"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEditBaseUnitClicked({
                                unit: row.unit,
                                id: row.id,
                            });
                        }}
                    />
                </div>
            ),
        },
    ];

    const subKeys = [
        {
            name: "unit",
        },
        {
            name: "equation",
        },
        {
            name: "description",
            element: (row) => (
                <Tippy
                    content={row.description}
                    placement="top"
                    animation="scale"
                >
                    <span tabIndex="0" className="tooltip-container">
                        {row.description}
                    </span>
                </Tippy>
            ),
        },
        {
            name: "actions",
            element: (row) => (
                <div className="flex items-center justify-center">
                    <BaseActionButton
                        className="table-btn table-btn-blue mr-2"
                        icon="fas fa-tools"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEditConversionUnitClicked(row);
                        }}
                    />
                    <DeleteButton
                        onClick={() => {
                            setToBeDeletedId(row.id);
                            setDeleteModal(true);
                        }}
                        tippy={true}
                    />
                </div>
            ),
        },
    ];

    const filterOptions = [
        {
            label: t("unit"),
            name: "unit",
            type: "Input",
            placeholder: t("unitPlaceholder"),
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
        },
    ];

    useEffect(() => {
        if (sortBy && sortDirection) {
            setCurrentPage(1);
            listUnits(appliedFilters, sortBy, sortDirection);
        } else {
            setCurrentPage(1);
            listUnits(appliedFilters);
        }
    }, [sortBy, sortDirection]);

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Unit-Management-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        listUnits(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            listUnits(filters, sortBy, sortDirection, index - 1);
        }
    };

    const listUnits = async (
        filters,
        defaultSortBy,
        defaultSortDirection,
        offset,
        itemsPerPage = itemsPerRequest || 10
    ) => {
        setIsLoading(true);
        const result = await UnitsAPI.listUnits(
            itemsPerPage,
            getMappedFilters(filters),
            offset,
            defaultSortBy,
            defaultSortDirection
        );

        setTotalUnitsCount(result?.totalCount || 0);
        setUnits(result?.data || []);

        setIsLoading(false);
    };

    const addBaseUnit = async () => {
        const result = await UnitsAPI.addUnit(selectedBaseUnit);
        if (result?.inserted) {
            SuccessAlert(t("addSuccessMessage", { entity: t("unit") }));
            setBaseUnitModal(false);
            listUnits(appliedFilters, sortBy, sortDirection);
        }
    };

    const editBaseUnit = async () => {
        const result = await UnitsAPI.updateUnit(selectedBaseUnit);
        if (result?.modified) {
            SuccessAlert(t("updateSuccessMessage", { entity: t("unit") }));
            updateUnitNameLocally(selectedBaseUnit.id, selectedBaseUnit.unit);
            setBaseUnitModal(false);
        }
    };

    const addConvertionUnit = async (payload) => {
        const result = await UnitConversionAPI.addConversionUnit(payload);
        if (result?.inserted) {
            SuccessAlert(
                t("addSuccessMessage", { entity: t("conversionUnit") })
            );
            setConversionUnitModal(false);
            listUnits(appliedFilters, sortBy, sortDirection);
        }
    };

    const editConvertionUnit = async (payload) => {
        const result = await UnitConversionAPI.updateConversionUnit(payload);
        if (result?.modified) {
            SuccessAlert(
                t("updateSuccessMessage", { entity: t("conversionUnit") })
            );
            setConversionUnitModal(false);
            listUnits(appliedFilters, sortBy, sortDirection);
        }
    };

    const deleteConversionUnit = async () => {
        const result = await UnitConversionAPI.removeConversionUnit(
            toBeDeletedId
        );
        if (result?.deleted) {
            SuccessAlert(
                t("deleteSuccessMessage", { entity: t("conversionUnit") })
            );
            setToBeDeletedId("");
            listUnits(appliedFilters, sortBy, sortDirection);
        }
    };

    const updateUnitNameLocally = (unitId, name) => {
        setUnits((prevUnits) =>
            prevUnits.map((unit) =>
                unit.id === unitId ? { ...unit, unit: name } : unit
            )
        );
    };

    const handleEditBaseUnitClicked = (unit) => {
        setSelectedBaseUnit(unit);
        setBaseUnitModal(true);
    };

    const handleEditConversionUnitClicked = (unit) => {
        setSelectedConvertionUnit(unit);
        setConversionUnitModal(true);
    };

    const applyFilters = (specificAppliedFilters) => {
        listUnits(specificAppliedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="unitsManagement">
                            Units Management
                        </Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={filterOptions}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component={"unit-management"}
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />
                        <button
                            className="default-orange-outline-btn ml-2"
                            onClick={() => {
                                setBaseUnitModal(true);
                            }}
                        >
                            <i className="fas fa-plus mr-2" />
                            <Trans i18nKey="newUnit">New Unit</Trans>
                        </button>
                    </div>
                </header>
            </div>
            <NestedTable
                headers={headers}
                subHeaders={subHeaders}
                keys={keys}
                subKeys={subKeys}
                rows={units}
                subRowsKey="unitConversions"
                isLoading={isLoading}
                isFilterUsed={filtersUsed}
                entity={t("unitsManagement")}
                currentPage={currentPage}
                onPageChange={handlePageClick}
                totalCount={totalUnitsCount}
                sizePerPage={itemsPerRequest}
                handleItemsPerPage={handleItemsPerPage}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                missingSubRowMessage={t("conversionUnitsMissingMessage")}
            />
            <BaseUnitModal
                isVisible={baseUnitModal}
                setIsVisible={setBaseUnitModal}
                editAction={editBaseUnit}
                addAction={addBaseUnit}
                unit={selectedBaseUnit}
                setUnit={setSelectedBaseUnit}
            />
            <ConversionUnitModal
                isVisible={conversionUnitModal}
                setIsVisible={setConversionUnitModal}
                conversionUnit={selectedConvertionUnit}
                setConversionUnit={setSelectedConvertionUnit}
                addConvertionUnitAction={addConvertionUnit}
                editConvertionUnitAction={editConvertionUnit}
            />
            <BaseAlertModal
                isVisible={deleteModal}
                setIsVisible={setDeleteModal}
                title={t("deleteTitle", { purpose: t("conversionUnit") })}
                message={t("deleteMessage", { purpose: t("conversionUnit") })}
                action={{ submitAction: deleteConversionUnit }}
                type="delete"
            />
        </>
    );
};

export default UnitsManagement;
